import { useState } from 'react';
import {
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  Card,
  Modal,
  InputNumber,
  Tag,
  message,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
} from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;

// Field types available
export const FIELD_TYPES = {
  text: { label: 'Texto', icon: '📝' },
  email: { label: 'E-mail', icon: '📧' },
  phone: { label: 'Telefone', icon: '📞' },
  number: { label: 'Número', icon: '🔢' },
  cpf: { label: 'CPF', icon: '👤' },
  cnpj: { label: 'CNPJ', icon: '🏢' },
  date: { label: 'Data', icon: '📅' },
  select: { label: 'Seleção Única', icon: '📋' },
  'multi-select': { label: 'Seleção Múltipla', icon: '☑️' },
  textarea: { label: 'Texto Longo', icon: '📄' },
  checkbox: { label: 'Checkbox', icon: '✅' },
  radio: { label: 'Radio', icon: '🔘' },
} as const;

export type FieldType = keyof typeof FIELD_TYPES;

export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required: boolean;
  validation?: Record<string, any>;
  options?: string[]; // For select, multi-select, radio
  defaultValue?: string;
  order: number;
  n8n_name?: string;
}

export interface FormBuilderProps {
  fields: FormField[];
  onChange: (fields: FormField[]) => void;
}

interface FieldModalProps {
  visible: boolean;
  field: FormField | null;
  onSave: (field: FormField) => void;
  onCancel: () => void;
}

// Modal to add/edit a field
const FieldModal = ({ visible, field, onSave, onCancel }: FieldModalProps) => {
  const [form] = Form.useForm();
  const [fieldType, setFieldType] = useState<FieldType>(field?.type || 'text');

  const needsOptions = ['select', 'multi-select', 'radio'].includes(fieldType);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      
      // Parse options if needed
      let options: string[] | undefined;
      if (needsOptions && values.optionsText) {
        options = values.optionsText
          .split('\n')
          .map((opt: string) => opt.trim())
          .filter((opt: string) => opt.length > 0);
      }

      const newField: FormField = {
        id: field?.id || `field_${Date.now()}`,
        type: values.type,
        label: values.label,
        placeholder: values.placeholder,
        required: values.required || false,
        defaultValue: values.defaultValue,
        order: field?.order || 0,
        options,
        n8n_name: values.n8n_name || undefined,
      };

      onSave(newField);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={field ? 'Editar Campo' : 'Adicionar Campo'}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={600}
      okText="Salvar"
      cancelText="Cancelar"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={
          field
            ? {
                ...field,
                optionsText: field.options?.join('\n') || '',
              }
            : {
                type: 'text',
                required: false,
              }
        }
      >
        <Form.Item
          name="type"
          label="Tipo do Campo"
          rules={[{ required: true, message: 'Selecione o tipo do campo' }]}
        >
          <Select onChange={(value) => setFieldType(value as FieldType)}>
            {Object.entries(FIELD_TYPES).map(([key, { label, icon }]) => (
              <Option key={key} value={key}>
                {icon} {label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="label"
          label="Rótulo"
          rules={[{ required: true, message: 'Digite o rótulo do campo' }]}
        >
          <Input placeholder="Ex: Nome Completo" />
        </Form.Item>
        
        <Form.Item name="n8n_name" label="Field name N8N" tooltip="Nome da variável usada no n8n (opcional)">
          <Input placeholder="Ex: first_name" />
        </Form.Item>

        <Form.Item name="placeholder" label="Placeholder">
          <Input placeholder="Ex: Digite seu nome" />
        </Form.Item>

        {needsOptions && (
          <Form.Item
            name="optionsText"
            label="Opções (uma por linha)"
            rules={[{ required: true, message: 'Digite as opções' }]}
          >
            <TextArea
              rows={4}
              placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
            />
          </Form.Item>
        )}

        <Form.Item name="defaultValue" label="Valor Padrão">
          <Input />
        </Form.Item>

        <Form.Item name="required" label="Campo Obrigatório" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const FormBuilder = ({ fields, onChange }: FormBuilderProps) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  const handleAddField = () => {
    setEditingField(null);
    setModalVisible(true);
  };

  const handleEditField = (field: FormField) => {
    setEditingField(field);
    setModalVisible(true);
  };

  const handleSaveField = (field: FormField) => {
    if (editingField) {
      // Update existing field
      const updatedFields = fields.map((f) =>
        f.id === field.id ? field : f
      );
      onChange(updatedFields);
      message.success('Campo atualizado com sucesso!');
    } else {
      // Add new field
      const newField = { ...field, order: fields.length };
      onChange([...fields, newField]);
      message.success('Campo adicionado com sucesso!');
    }
    setModalVisible(false);
    setEditingField(null);
  };

  const handleDeleteField = (fieldId: string) => {
    Modal.confirm({
      title: 'Confirmar exclusão',
      content: 'Tem certeza que deseja excluir este campo?',
      okText: 'Sim',
      cancelText: 'Não',
      onOk: () => {
        const updatedFields = fields
          .filter((f) => f.id !== fieldId)
          .map((f, index) => ({ ...f, order: index }));
        onChange(updatedFields);
        message.success('Campo excluído com sucesso!');
      },
    });
  };

  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === fields.length - 1)
    ) {
      return;
    }

    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newFields[index], newFields[targetIndex]] = [
      newFields[targetIndex],
      newFields[index],
    ];

    // Update order
    const reorderedFields = newFields.map((f, i) => ({ ...f, order: i }));
    onChange(reorderedFields);
  };

  return (
    <div className="form-builder">
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddField}
          >
            Adicionar Campo
          </Button>
          <Button
            icon={<EyeOutlined />}
            onClick={() => setPreviewVisible(true)}
            disabled={fields.length === 0}
          >
            Visualizar Formulário
          </Button>
        </Space>
      </div>

      {fields.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            <p>Nenhum campo adicionado ainda</p>
            <p>Clique em "Adicionar Campo" para começar</p>
          </div>
        </Card>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {fields
            .sort((a, b) => a.order - b.order)
            .map((field, index) => (
              <Card
                key={field.id}
                size="small"
                title={
                  <Space>
                    <span>
                      {FIELD_TYPES[field.type].icon} {field.label}
                    </span>
                    {field.required && <Tag color="red">Obrigatório</Tag>}
                    <Tag color="blue">{FIELD_TYPES[field.type].label}</Tag>
                  </Space>
                }
                extra={
                  <Space>
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowUpOutlined />}
                      onClick={() => handleMoveField(index, 'up')}
                      disabled={index === 0}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowDownOutlined />}
                      onClick={() => handleMoveField(index, 'down')}
                      disabled={index === fields.length - 1}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEditField(field)}
                    />
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteField(field.id)}
                    />
                  </Space>
                }
              >
                {field.placeholder && (
                  <p style={{ margin: 0, color: '#999' }}>
                    Placeholder: {field.placeholder}
                  </p>
                )}
                {field.options && (
                  <p style={{ margin: 0, color: '#666' }}>
                    Opções: {field.options.join(', ')}
                  </p>
                )}
              </Card>
            ))}
        </Space>
      )}

      <FieldModal
        visible={modalVisible}
        field={editingField}
        onSave={handleSaveField}
        onCancel={() => {
          setModalVisible(false);
          setEditingField(null);
        }}
      />

      {/* Preview Modal */}
      <Modal
        title="Visualização do Formulário"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Fechar
          </Button>,
        ]}
        width={700}
      >
        <Form layout="vertical">
          {fields
            .sort((a, b) => a.order - b.order)
            .map((field) => (
              <Form.Item
                key={field.id}
                label={field.label}
                required={field.required}
              >
                {field.type === 'textarea' ? (
                  <TextArea placeholder={field.placeholder} rows={4} />
                ) : field.type === 'select' ? (
                  <Select placeholder={field.placeholder}>
                    {field.options?.map((opt) => (
                      <Option key={opt} value={opt}>
                        {opt}
                      </Option>
                    ))}
                  </Select>
                ) : field.type === 'multi-select' ? (
                  <Select mode="multiple" placeholder={field.placeholder}>
                    {field.options?.map((opt) => (
                      <Option key={opt} value={opt}>
                        {opt}
                      </Option>
                    ))}
                  </Select>
                ) : field.type === 'checkbox' ? (
                  <Switch />
                ) : field.type === 'number' ? (
                  <InputNumber placeholder={field.placeholder} style={{ width: '100%' }} />
                ) : (
                  <Input
                    type={field.type === 'email' ? 'email' : field.type === 'date' ? 'date' : 'text'}
                    placeholder={field.placeholder}
                  />
                )}
              </Form.Item>
            ))}
        </Form>
      </Modal>
    </div>
  );
};
